import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { User, ClipboardList, AlertTriangle, CheckCircle2, ShieldAlert, ChevronDown, ChevronRight, Trash2, Users, FolderKanban, Sparkles, ArrowRight } from "lucide-react";

/** localStorage-Klientenakte — sammelt Aufnahmen, Ampel-Verläufe und
 *  Dokuketten pro Bewohner chronologisch. Daten bleiben im Browser
 *  (Paradygma-Stil: "keine automatische Übertragung"). */

const AMPEL_ICON = { gruen: CheckCircle2, gelb: AlertTriangle, rot: ShieldAlert };
const AMPEL_BG = { gruen: "bg-emerald-500", gelb: "bg-amber-500", rot: "bg-brand-darkred" };

const TYPE_META = {
  aufnahme:  { label: "Aufnahmegespräch", Icon: ClipboardList, tint: "bg-brand-darkred text-cream-50" },
  ampel:     { label: "Pflegeampel",      Icon: AlertTriangle, tint: "bg-terra text-white" },
  dokukette: { label: "Dokukette",        Icon: ClipboardList, tint: "bg-sand-800 text-cream-50" },
};

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function PkiKlientenakte() {
  const [store, setStore] = useState({});
  const [open, setOpen] = useState(null);

  useEffect(() => {
    try { setStore(JSON.parse(localStorage.getItem("dh-klienten-akte") || "{}")); }
    catch { setStore({}); }
  }, []);

  const persist = (next) => {
    setStore(next);
    try { localStorage.setItem("dh-klienten-akte", JSON.stringify(next)); }
    catch { /* noop */ }
  };

  const clientNames = Object.keys(store).sort();
  const totalCount = clientNames.reduce((sum, n) => sum + (store[n]?.length || 0), 0);

  const removeEntry = (name, idx) => {
    const next = { ...store, [name]: store[name].filter((_, i) => i !== idx) };
    if (next[name].length === 0) delete next[name];
    persist(next);
  };
  const removeClient = (name) => {
    if (!confirm(`Alle Einträge von "${name}" löschen?`)) return;
    const next = { ...store }; delete next[name]; persist(next);
    setOpen(null);
  };

  return (
    <div data-testid="pki-klientenakte" className="space-y-4">
      <div>
        <h2 className="font-display text-2xl text-sand-800 leading-tight">360°-Klientenakte</h2>
        <p className="text-[13.5px] text-sand-600 mt-1 leading-relaxed">
          Alle Aufnahmen, Ampeln und Dokuketten pro Bewohner:in chronologisch
          gebündelt. <strong>Daten bleiben in diesem Browser</strong> — keine
          automatische Übertragung.
        </p>
      </div>

      {/* Migration-CTA zur neuen Patientenmappe */}
      <div className="rounded-2xl bg-brand-darkred text-cream-50 p-4 sm:p-5 relative overflow-hidden">
        <div className="absolute -right-8 -top-8 opacity-10">
          <FolderKanban className="h-40 w-40" strokeWidth={0.8} />
        </div>
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-saffron/30 text-saffron px-2 py-0.5 text-[10px] font-bold tracking-widest mb-2">
            <Sparkles className="h-3 w-3" /> NEU · PARADYGMA-SUITE
          </div>
          <div className="font-display text-2xl leading-tight mb-1">Patientenmappe · 11 Sektionen</div>
          <p className="text-[13px] text-cream-100 max-w-lg leading-relaxed mb-3">
            SIS · Risikomatrix · Medikation · Maßnahmenplan · Versorgungsauftrag · 360°-Beobachtung
            · Vitalwerte · QPR-26 · Signaturen — alles zentral in MongoDB, Team-fähig, prüfbereit.
          </p>
          <Link to="/campus/app/patientenmappe" data-testid="pki-klientenakte-migrate-cta"
            className="inline-flex items-center gap-2 rounded-full bg-saffron text-brand-darkred px-4 py-2 text-[13.5px] font-semibold hover:bg-saffron-dark transition-colors">
            Patientenmappen öffnen <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      <div className="rounded-2xl bg-cream-50 border border-cream-300 p-4 flex items-center gap-3">
        <Users className="h-5 w-5 text-brand-darkred shrink-0" strokeWidth={1.9} />
        <div className="text-[13.5px] text-sand-700">
          <strong className="text-sand-800">{clientNames.length}</strong> Bewohner:innen (Legacy)
          <span className="mx-1.5">·</span>
          <strong className="text-sand-800">{totalCount}</strong> Einträge
        </div>
      </div>

      {clientNames.length === 0 && (
        <div data-testid="pki-klientenakte-empty" className="text-center py-14 rounded-2xl bg-white border border-cream-300">
          <User className="h-8 w-8 text-sand-400 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sand-600 text-[14px]">
            Noch keine Legacy-Einträge. Nutz die neue <Link to="/campus/app/patientenmappe" className="text-brand-darkred font-semibold underline">Patientenmappe</Link> —
            sie speichert Team-übergreifend in der Cloud.
          </p>
        </div>
      )}

      <ul className="space-y-3">
        {clientNames.map((name) => {
          const entries = store[name] || [];
          const isOpen = open === name;
          return (
            <li key={name} className="rounded-2xl bg-white border border-cream-300 overflow-hidden"
                data-testid="pki-klientenakte-client">
              <button type="button" onClick={() => setOpen(isOpen ? null : name)}
                className="w-full flex items-center gap-3 p-4 hover:bg-cream-50 transition-colors text-left">
                <span className="h-10 w-10 rounded-xl bg-brand-darkred text-cream-50 inline-flex items-center justify-center shrink-0 font-bold text-[13px]">
                  {name.split(" ").map((s) => s[0]?.toUpperCase() || "").join("").slice(0, 2) || "?"}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sand-800 text-[15px] truncate">{name}</div>
                  <div className="text-[12.5px] text-sand-600 mt-0.5">
                    {entries.length} Einträge · zuletzt {fmt(entries[0]?.ts || "")}
                  </div>
                </div>
                {isOpen ? <ChevronDown className="h-4 w-4 text-sand-500" strokeWidth={2} />
                        : <ChevronRight className="h-4 w-4 text-sand-500" strokeWidth={2} />}
              </button>

              {isOpen && (
                <div className="border-t border-cream-200 divide-y divide-cream-200">
                  {entries.map((e, i) => {
                    const meta = TYPE_META[e.typ] || TYPE_META.dokukette;
                    const AmpelIcon = e.ampel ? AMPEL_ICON[e.ampel] : null;
                    return (
                      <div key={i} className="p-4 sm:p-5" data-testid="pki-klientenakte-entry">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 h-6 text-[10.5px] font-bold uppercase tracking-[0.14em] ${meta.tint}`}>
                              <meta.Icon className="h-3 w-3" strokeWidth={2} /> {meta.label}
                            </span>
                            {e.ampel && AmpelIcon && (
                              <span className={`inline-flex items-center gap-1 rounded-full ${AMPEL_BG[e.ampel]} text-white px-2 h-6 text-[10.5px] font-bold uppercase`}>
                                <AmpelIcon className="h-3 w-3" strokeWidth={2} /> {e.ampel}
                              </span>
                            )}
                            <span className="text-[11.5px] text-sand-500 font-medium">{fmt(e.ts)}</span>
                          </div>
                          <button type="button" onClick={() => removeEntry(name, i)}
                            data-testid="pki-klientenakte-remove"
                            className="text-sand-400 hover:text-brand-darkred" aria-label="Eintrag löschen">
                            <Trash2 className="h-4 w-4" strokeWidth={1.7} />
                          </button>
                        </div>
                        {e.zusammenfassung && (
                          <p className="text-[13.5px] text-sand-700 leading-relaxed">{e.zusammenfassung}</p>
                        )}
                        {e.dokukette?.risiko && (
                          <div className="mt-2 text-[13px] text-sand-700 leading-relaxed">
                            <strong className="text-sand-800">Risiko:</strong> {e.dokukette.risiko}
                          </div>
                        )}
                        {e.dokukette?.massnahme && (
                          <div className="mt-1 text-[13px] text-sand-700 leading-relaxed">
                            <strong className="text-sand-800">Maßnahme:</strong> {e.dokukette.massnahme}
                          </div>
                        )}
                        {e.sofort?.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {e.sofort.slice(0, 3).map((s, k) => (
                              <li key={k} className="text-[13px] text-sand-700 flex items-start gap-1.5">
                                <span className="text-brand-darkred font-bold">·</span>
                                <span>{s}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                  <div className="p-3 bg-cream-50">
                    <button type="button" onClick={() => removeClient(name)}
                      className="text-[12.5px] text-sand-500 hover:text-brand-darkred font-semibold inline-flex items-center gap-1.5">
                      <Trash2 className="h-3 w-3" strokeWidth={1.8} /> Klient:in komplett entfernen
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
